import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  LayoutDashboard,
  Database,
  Package,
  Settings,
  LogOut,
  FileText,
  Home,
  Command,
  Download,
  Search,
  Activity,
  DollarSign,
  BarChart3,
  BookOpen,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import CommandPalette from './CommandPalette';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { getLaunchFlags } from '@/lib/config/flags';
import { getEscrowEnvDisplay } from '@/lib/diagnostics/envDiagnostics';
import { joinApi } from '@/lib/runtime/apiBase';

function LaunchFlagsBadges() {
  const flags = getLaunchFlags();
  const escrowEnv = getEscrowEnvDisplay();

  return (
    <>
      <Badge variant={flags.LAUNCH_MODE ? 'default' : 'secondary'} className="text-xs">
        {flags.LAUNCH_MODE ? 'LIVE' : 'PRE-LAUNCH'}
      </Badge>
      <Badge variant={escrowEnv.variant} className="text-xs">
        Escrow: {escrowEnv.label}
      </Badge>
      {flags.MAINTENANCE_MODE && (
        <Badge variant="destructive" className="text-xs">
          MAINTENANCE
        </Badge>
      )}
      {flags.CHECKOUT_DISABLED && (
        <Badge variant="outline" className="text-xs">
          CHECKOUT OFF
        </Badge>
      )}
    </>
  );
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  // Check authentication via cookie/session endpoint (P20250925-156: dual-path)
  useEffect(() => {
    let cancelled = false;
    const adminBasePath = '/manage';

    const checkAuth = async () => {
      try {
        const res = await fetch(joinApi('/api/manage/session'), {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
        });
        const data = await res.json().catch(() => ({}));

        if (data?.ok) {
          if (!cancelled) setIsAuthenticated(true);
          return;
        }
      } catch (error) {
        console.debug('[AdminLayout] Session check failed:', error);
      }

      if (!cancelled) {
        navigate(`${adminBasePath}/login`, { replace: true });
      }
    };

    if (!location.pathname.includes(`${adminBasePath}/login`)) {
      checkAuth();
    } else {
      setIsAuthenticated(true);
    }

    return () => {
      cancelled = true;
    };
  }, [navigate, location]);

  const handleLogout = async () => {
    const adminBasePath = '/manage';

    try {
      await fetch(joinApi('/api/manage/logout'), {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('[AdminLayout] Logout failed:', error);
    }
    navigate(`${adminBasePath}/login`, { replace: true });
  };

  // Show loading state while checking auth
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Verifying access...</div>
      </div>
    );
  }

  const adminBasePath = '/manage'; // P20250925-153: Changed from /admin
  const navItems = [
    { path: `${adminBasePath}`, label: 'Hub', icon: Home, exact: true },
    { path: `${adminBasePath}/dashboard`, label: 'Dashboard', icon: LayoutDashboard },
    { path: `${adminBasePath}/owner`, label: 'Owner Pack', icon: LayoutDashboard },
    { path: `${adminBasePath}/data`, label: 'Data Hub', icon: Database },
    { path: `${adminBasePath}/data-governance`, label: 'Data Governance', icon: Settings },
    { path: `${adminBasePath}/launch`, label: 'Launch', icon: Activity },
    { path: `${adminBasePath}/inventory`, label: 'Inventory', icon: Package },
    { path: `${adminBasePath}/pricing`, label: 'Pricing', icon: DollarSign },
    { path: `${adminBasePath}/metrics`, label: 'Metrics', icon: BarChart3 },
    { path: `${adminBasePath}/runbook`, label: 'Runbook', icon: BookOpen },
    { path: `${adminBasePath}/experiments`, label: 'Experiments', icon: FileText },
    { path: `${adminBasePath}/prompts`, label: 'Prompts', icon: FileText },
    { path: `${adminBasePath}/exports`, label: 'Exports', icon: Download },
    { path: `${adminBasePath}/diagnostics`, label: 'Diagnostics', icon: Activity },
    { path: `${adminBasePath}/env`, label: 'Environment', icon: Settings },
    { path: `${adminBasePath}/seo`, label: 'SEO', icon: Search },
    { path: `${adminBasePath}/users`, label: 'Users', icon: Settings },
    { path: `${adminBasePath}/cancellations`, label: 'Cancellations', icon: Settings },
    { path: `${adminBasePath}/settings`, label: 'Settings', icon: Settings },
  ];

  // Generate breadcrumbs
  const getBreadcrumbs = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts[0] !== 'manage') return [];

    const crumbs = [{ label: 'Admin', path: adminBasePath }];
    if (pathParts.length > 1) {
      const current = navItems.find((item) => item.path === location.pathname);
      if (current) {
        crumbs.push({ label: current.label, path: current.path });
      }
    }
    return crumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex,nofollow" />
        <meta httpEquiv="X-Robots-Tag" content="noindex,nofollow" />
      </Helmet>

      <CommandPalette />

      <div className="min-h-screen bg-background flex flex-col">
        {/* Top Header with Breadcrumbs */}
        <header className="border-b bg-card px-6 py-3">
          <div className="flex items-center justify-between">
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((crumb, i) => (
                  <React.Fragment key={crumb.path}>
                    {i > 0 && <BreadcrumbSeparator />}
                    <BreadcrumbItem>
                      {i === breadcrumbs.length - 1 ? (
                        <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink asChild>
                          <Link to={crumb.path}>{crumb.label}</Link>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>

            <div className="flex items-center gap-2">
              <LaunchFlagsBadges />
              <Badge variant="outline" className="text-xs">
                <Command className="w-3 h-3 mr-1" />
                Press ⌘K
              </Badge>
            </div>
          </div>
        </header>

        <div className="flex flex-1">
          {/* Sidebar */}
          <aside className="w-64 border-r bg-card flex flex-col">
            <div className="p-6">
              <h2 className="text-lg font-bold text-foreground">Owner Portal</h2>
              <p className="text-sm text-muted-foreground">Attain Domains Admin</p>
            </div>

            <nav className="px-4 space-y-1 flex-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = item.exact
                  ? location.pathname === item.path
                  : location.pathname === item.path;

                return (
                  <Link key={item.path} to={item.path}>
                    <Button
                      variant={isActive ? 'secondary' : 'ghost'}
                      className="w-full justify-start"
                    >
                      <Icon className="w-4 h-4 mr-3" />
                      {item.label}
                    </Button>
                  </Link>
                );
              })}
            </nav>

            <div className="p-4 border-t">
              <Button
                variant="ghost"
                className="w-full justify-start text-muted-foreground"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-3" />
                Logout
              </Button>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
}
